import { useApps } from '@auth/useApps'
import { colors } from '@globals/themes/defaultTheme'
import { tabletQuery } from '@globals/themes/themeConstants'
import { getRecognitionGoal } from '@kudos/http-client'
import StarIcon from '@mui/icons-material/Star'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { AppPermission } from '@shared/permissions'
import { useQuery } from '@tanstack/react-query'
import { T } from '@transifex/react'
import { FC, Fragment } from 'react'

export const RECONITION_GOAL_QK = 'recognitionGoal'

const RecognitionCounter: FC = () => {
  const [hasPermission] = useApps()
  const isTablet = useMediaQuery(tabletQuery)
  const canBrowseRecognitions = hasPermission('recognitionApp', AppPermission.RecognitionAppBrowse)
  const { data } = useQuery({
    queryKey: [RECONITION_GOAL_QK],
    queryFn: () => getRecognitionGoal(),
    // Note that we do not want the cache to clear and we do not want data to be
    // set as stale.
    // If the user sends a recognition, we invalidate this query
    // and retrieve the latest data again.
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: canBrowseRecognitions,
  })

  if (!data || !canBrowseRecognitions) {
    return null
  }

  const { count, goal } = data

  return (
    <Stack
      direction="column"
      useFlexGap
      spacing={1}
      alignItems={isTablet ? 'flex-start' : 'center'}>
      <Typography variant="body1" color={colors.white} textAlign={isTablet ? undefined : 'center'}>
        <T
          _str="Send {goal} recognition messages to reach your monthly goal"
          goal={goal.toLocaleString()}
        />
      </Typography>
      <Stack direction="row" alignItems="center" mt={{ xs: 0 }}>
        {Array.from({ length: goal }).map((_, index) => (
          <Fragment key={index}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width={24}
              height={24}
              borderRadius="50%"
              sx={{
                backgroundColor: index < count ? colors.goldPrimary : colors.goldExtraLight,
                border: '4px solid',
                borderColor: colors.goldPrimary,
              }}>
              {index < count && <StarIcon sx={{ color: colors.goldExtraLight }} />}
            </Box>
            {index < goal - 1 && <Box width={16} height={4} bgcolor={colors.goldPrimary} />}
          </Fragment>
        ))}
      </Stack>
    </Stack>
  )
}

export default RecognitionCounter
